import type { EntityState } from '@reduxjs/toolkit';

import { createEntityAdapterSchema } from '../../base/primitives';
import {
  type IntegrationConfig,
  IntegrationConfigSchema,
} from './integration.schema';

/**
 * IntegrationsCollection uses Redux Toolkit EntityAdapter
 * Structure: { ids: string[], entities: { [provider: string]: IntegrationConfig } }
 * Now stores one config per provider with multiple environment connections
 */
export type IntegrationsCollection = EntityState<IntegrationConfig, string>;

/**
 * Zod schema for validating IntegrationsCollection
 */
export const IntegrationsCollectionSchema = createEntityAdapterSchema(
  IntegrationConfigSchema,
);

/**
 * Validate that no duplicate connections exist in the same environment
 * This should be called when projecting events or validating state
 */
export function validateIntegrationCollection(
  collection: IntegrationsCollection,
): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  const configs = Object.values(collection.entities).filter(
    (c): c is IntegrationConfig => c !== undefined,
  );

  // Track connections by environment
  const connectionsByEnv: Record<string, Set<string>> = {
    sandbox: new Set(),
    preRelease: new Set(),
    live: new Set(),
  };

  configs.forEach(config => {
    // Check each environment for this provider
    Object.entries(config.clientEnvs).forEach(([env, connectionId]) => {
      if (connectionId) {
        // connectionId is now just a string reference
        const connectionKey = `${config.provider}:${connectionId}`;

        // Check if this exact connection is already in another environment
        Object.entries(connectionsByEnv).forEach(([otherEnv, connections]) => {
          if (otherEnv !== env && connections.has(connectionKey)) {
            errors.push(
              `Connection ${connectionId} for ${config.provider} is enabled in multiple environments: ${env} and ${otherEnv}`,
            );
          }
        });

        connectionsByEnv[env].add(connectionKey);
      }
    });
  });

  return {
    valid: errors.length === 0,
    errors,
  };
}
